import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Settings, Sliders, Trash2, Server, Key, GitCommit, Globe, Database, Brain, Cable } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { HelpSystem } from '../../components/tour';

// Import section components
import GeneralSection from '../project/GeneralSection';
import PublishSection from '../project/PublishSection';
import DeleteProjectSection from '../project/DeleteProjectSection';
import EnvironmentVariablesSection from '../project/EnvironmentVariablesSection';
import RevisionSection from '../project/RevisionSection';
import DomainsSection from '../project/DomainsSection';
import DatabaseSection from '../project/DatabaseSection';
import MemoriesSection from '../project/MemoriesSection';
import McpServersSection from '../project/McpServersSection';

// Define sections outside of component for export access
const PROJECT_SECTIONS = [
  { id: 'environment', label: 'Environment Variables', icon: Key, component: EnvironmentVariablesSection },
  { id: 'mcp', label: 'MCP Servers', icon: Cable, component: McpServersSection },
  { id: 'publish', label: 'Publish', icon: Server, component: PublishSection },
  { id: 'dns', label: 'Custom Domain', icon: Globe, component: DomainsSection },
  { id: 'database', label: 'Database', icon: Database, component: DatabaseSection },
  { id: 'memories', label: 'Memories', icon: Brain, component: MemoriesSection },
  { id: 'revisions', label: 'Revisions', icon: GitCommit, component: RevisionSection },
  { id: 'general', label: 'General', icon: Sliders, component: GeneralSection },
  { id: 'delete', label: 'Delete Project', icon: Trash2, component: DeleteProjectSection },
];

// Export sections IDs for validation
export const getProjectSectionIds = (): string[] => 
  PROJECT_SECTIONS.map(section => section.id);

interface Workspace {
  id: string;
  name: string;
  directory_path: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  deleted?: boolean;
  deleted_at?: string;
  scheduled_hard_delete_at?: string;
  template_slug?: string;
  mcp_servers?: any[]; // Added for MCP support
}

const ProjectSettingsModal: React.FC = () => {
  const api = useAuthenticatedApi();
  const { isProjectSettingsOpen, closeProjectSettings, currentWorkspaceId, initialSection } = useSettingsModal();
  const workspaceId = currentWorkspaceId;

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string>('environment');
  
  // Set initial section if provided by context
  useEffect(() => {
    if (initialSection && isProjectSettingsOpen) {
      setCurrentSection(initialSection);
    }
  }, [initialSection, isProjectSettingsOpen]);

  // Filter sections based on feature flags and template
  const sections = useMemo(() => {
    return PROJECT_SECTIONS.filter(section => {
      if (section.id === 'dns') {
        // Hide custom domains for Expo templates
        if (workspace?.template_slug === 'expo-template') {
          return false;
        }
      }
      return true;
    });
  }, [workspace?.template_slug]);

  // Ensure current section is valid when sections change (e.g., switching between Expo and non-Expo)
  useEffect(() => {
    if (!loading && sections.length > 0) {
      const currentSectionExists = sections.find(s => s.id === currentSection);
      if (!currentSectionExists) {
        // Reset to first available section if current section is hidden
        setCurrentSection(sections[0].id);
      }
    }
  }, [sections, currentSection, loading]);

  const fetchWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}`);
      const data = response.data || response;
      setWorkspace(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading workspace:', err);
      setError(err.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, api]);

  useEffect(() => {
    if (isProjectSettingsOpen && workspaceId) {
      fetchWorkspace();
    }
  }, [isProjectSettingsOpen, workspaceId, fetchWorkspace]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isProjectSettingsOpen) {
        closeProjectSettings();
      }
    };

    if (isProjectSettingsOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isProjectSettingsOpen, closeProjectSettings]);

  const handleSectionChange = (sectionId: string) => {
    setCurrentSection(sectionId);
  };

  const currentSectionData = sections.find(section => section.id === currentSection);
  const CurrentSectionComponent = currentSectionData?.component || GeneralSection;

  if (!isProjectSettingsOpen) {
    return null;
  }

  // Don't show modal if no workspace ID is available
  if (!workspaceId) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={closeProjectSettings}
    >
      <div 
        className="bg-white dark:bg-neutral-900 rounded-xl w-full h-full lg:w-[1200px] lg:h-[800px] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col shadow-2xl border border-neutral-200 dark:border-neutral-700 ring-1 ring-black/5 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center">
            <Settings className="w-6 h-6 text-neutral-600 dark:text-neutral-400 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Project Settings</h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">Manage your project configuration and settings</p>
            </div>
          </div>
          <button
            onClick={closeProjectSettings}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            data-tour="close-settings"
          >
            <X size={24} className="text-neutral-500 dark:text-neutral-400" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto lg:overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
          {/* Error Message */}
          {error && !loading && (
            <div className="m-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex flex-col lg:flex-row min-h-full lg:h-full gap-4 lg:gap-0 p-4 lg:p-0">
            {loading ? (
              <>
                {/* Sidebar placeholder during loading */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div
                          key={section.id}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-2 text-neutral-400 dark:text-neutral-500"
                        >
                          <Icon size={18} />
                          {section.label}
                        </div>
                      );
                    })}
                  </nav>
                </div>
                {/* Content area with loading */}
                <div className="flex-1 bg-white dark:bg-neutral-900 lg:bg-white lg:dark:bg-neutral-900 lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-white dark:bg-neutral-800 rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-neutral-900 h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
                  </div>
                </div>
              </>
            ) : error && !workspace ? (
              <>
                {/* Sidebar placeholder during error */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div
                          key={section.id}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-2 text-neutral-400 dark:text-neutral-500"
                        >
                          <Icon size={18} />
                          {section.label}
                        </div>
                      );
                    })}
                  </nav>
                </div>
                {/* Content area with error */}
                <div className="flex-1 bg-white dark:bg-neutral-900 lg:bg-white lg:dark:bg-neutral-900 lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-white dark:bg-neutral-800 rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-neutral-900 h-full flex items-center justify-center">
                    <div className="text-red-500">Error: {error}</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Sidebar Navigation */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      const isActive = section.id === currentSection;

                      return (
                        <button
                          key={section.id}
                          onClick={() => handleSectionChange(section.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 mb-2 ${isActive
                            ? 'bg-brand-primary text-black shadow-sm'
                            : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            }`}
                          data-tour={`settings-${section.id}`}
                        >
                          <Icon size={18} />
                          {section.label}
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 bg-white dark:bg-neutral-900 lg:bg-white lg:dark:bg-neutral-900 lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-white dark:bg-neutral-800 rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-neutral-900 p-4 lg:p-6 h-full">
                    <CurrentSectionComponent workspace={workspace} workspaceId={workspaceId} api={api} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <HelpSystem pageName="projectSettings" />
    </div>
  );
};

export default ProjectSettingsModal;